import { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box } from '@twilio-paste/core/box';
import {
  Sidebar,
  SidebarBody,
  SidebarCollapseButton,
  SidebarFooter,
  SidebarHeader,
  SidebarHeaderIconButton,
  SidebarHeaderLabel,
  SidebarNavigation,
  SidebarNavigationItem,
  SidebarPushContentWrapper,
} from '@twilio-paste/core/sidebar';
import { HistoryIcon } from '@twilio-paste/icons/esm/HistoryIcon';
import { ProductBillingIcon } from '@twilio-paste/icons/esm/ProductBillingIcon';
import { ProductHomeIcon } from '@twilio-paste/icons/esm/ProductHomeIcon';
import { ProductSupportIcon } from '@twilio-paste/icons/esm/ProductSupportIcon';

import {
  oscLayoutCopy,
  routes,
} from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';

import { constants, types } from '../definitions';
import { useCurrentPlan, useUserDetails } from '../../../hooks';
import { trackingDetails } from '../../../definitions/constants';

import styles from '../layout.module.css';

const CompositeSidebar = ({
  children,
  isCollapsed,
  onToggleCollapse,
}: types.SidebarNavigationProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { account, user, isLoading, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoggedIn,
    isLoading,
  });
  const { hasDeveloperSupport } = useCurrentPlan();

  const isRouteSelected = (route: string): boolean =>
    location.pathname === route;

  return (
    <Box as="div" className={styles.sidebarNavigation}>
      <Sidebar
        collapsed={isCollapsed}
        variant="compact"
        mainContentSkipLinkID={constants.navigationSkipLinks.mainContent}
        sidebarNavigationSkipLinkID={constants.navigationSkipLinks.sidebar}
        topbarSkipLinkID={constants.navigationSkipLinks.topbar}
      >
        <SidebarHeader>
          <SidebarHeaderIconButton
            as="button"
            onClick={(): void => {
              const eventName = trackingDetails.sidebarNavigation.homeClicked;
              const trackingContext = {
                route: routes.home,
                label: oscLayoutCopy.sidebar.header.label,
              };

              trackEvent(eventName, trackingContext);
              navigate(routes.home);
            }}
          >
            <ProductHomeIcon size="sizeIcon20" decorative />
          </SidebarHeaderIconButton>

          <SidebarHeaderLabel>
            {oscLayoutCopy.sidebar.header.label}
          </SidebarHeaderLabel>
        </SidebarHeader>

        <SidebarBody>
          <SidebarNavigation aria-label={oscLayoutCopy.sidebar.nav.ariaLabel}>
            {!hasDeveloperSupport && (
              <SidebarNavigationItem
                href={undefined}
                onClick={(): void => {
                  const eventName =
                    trackingDetails.sidebarNavigation.ticketSubmissionClicked;
                  const trackingContext = {
                    route: routes.ticketSubmission,
                    label: oscLayoutCopy.sidebar.nav.submitTicket,
                  };

                  trackEvent(eventName, trackingContext);
                  navigate(routes.ticketSubmission);
                }}
                selected={isRouteSelected(routes.ticketSubmission)}
                icon={<ProductSupportIcon decorative />}
              >
                {oscLayoutCopy.sidebar.nav.submitTicket}
              </SidebarNavigationItem>
            )}

            <SidebarNavigationItem
              href={undefined}
              onClick={(): void => {
                const eventName =
                  trackingDetails.sidebarNavigation.supportPlansClicked;
                const trackingContext = {
                  route: routes.supportPlans,
                  label: oscLayoutCopy.sidebar.nav.supportPlans,
                };

                trackEvent(eventName, trackingContext);
                navigate(routes.supportPlans);
              }}
              selected={isRouteSelected(routes.supportPlans)}
              icon={<ProductBillingIcon decorative />}
            >
              {oscLayoutCopy.sidebar.nav.supportPlans}
            </SidebarNavigationItem>

            <SidebarNavigationItem
              href={undefined}
              onClick={(): void => {
                const eventName =
                  trackingDetails.sidebarNavigation.ticketHistoryClicked;
                const trackingContext = {
                  route: routes.ticketHistory,
                  label: oscLayoutCopy.sidebar.nav.ticketHistory,
                };

                trackEvent(eventName, trackingContext);
                navigate(routes.ticketHistory);
              }}
              selected={isRouteSelected(routes.ticketHistory)}
              icon={<HistoryIcon decorative />}
            >
              {oscLayoutCopy.sidebar.nav.ticketHistory}
            </SidebarNavigationItem>
          </SidebarNavigation>
        </SidebarBody>

        <SidebarFooter>
          <SidebarCollapseButton
            i18nCollapseLabel={
              oscLayoutCopy.sidebar.collapseButton.collapseLabel
            }
            i18nExpandLabel={oscLayoutCopy.sidebar.collapseButton.expandLabel}
            onClick={onToggleCollapse}
          />
        </SidebarFooter>
      </Sidebar>

      <SidebarPushContentWrapper collapsed={isCollapsed} variant="compact">
        {children}
      </SidebarPushContentWrapper>
    </Box>
  );
};

export default CompositeSidebar;
